import { OptionedIcon, PurchaseItem, selectOption } from '@/types'
import { deepEqual, fetchEnvironmentUrl } from '@/utils/helper'
import { createContext, ReactNode, useCallback, useMemo, useState } from 'react'

const baseUrl = fetchEnvironmentUrl()
interface DealerContextProps {
  setDealer: (dealer: DealerDetails) => void
  setDealerBasicThemeDetails: (themeDetails: ThemeDetails) => void
  setDealerLoadingCompleted: (isLoaded: boolean) => void
  isApplicationLoaded: boolean
  buttonShape: string,
  dealerInformation: dealerInformation,
  callBackEnabled: boolean
  dealerThemeIcon: ThemeIcon
  themeLogo: string
  isDealer: boolean
}

interface dealerInformation {
  image: string
  name: string
  minimumLoanAmount: number,
  buyingOptions: string[],
  make: selectOption[],
  model: selectOption[],
  dealerId: number | null,
  dealerUniqueIdentifier: string | null,
  phoneNumber: string
}

interface DealerDetails {
  id: number
  nodeId: number
  dealerId: number
  dealerUniqueIdentifier: string
  dealerName: string
  dealerPhoneNumber: string
  dealerContactEmails: string[]
  region: string
  dealerPdfPassword: string
  minimumLoanAmount: number
  make: string[] | null
  model: string[] | null
  buyingOptions: string
  showBusinessAll: boolean
  callBackEnabled: boolean
  themeDetails: ThemeDetails
}

interface ThemeIcon {
  individual: string | null
  business: string | null
  nzLicense: string | null
  employmentDetails: string | null
  financialStanding: string | null
  requestCallBack: string | null
  continueApplication: string | null
  buyingOptions: PurchaseItem[]
  personalFinancialPosition: OptionedIcon[]
  businessFinancialPosition: OptionedIcon[]
  contact?: string | null
  email?: string | null
  timeToProcess?: string | null
}

interface ThemeDetails {
  id: number
  nodeId: number
  name: string
  brand: string | null
  highlyCustomized: boolean
  logo: string
  image: string
  color: {
    primary: string
    secondary: string
    tertiary: string
    text: string
    icon: string
  }
  buttonShape: '' | 'Main' | 'Square' | 'Rounded'
  regularFont: string
  regularFontWeight: string
  boldFont: string
  boldFontWeight: string
  icon: ThemeIcon
}

const DealerContext = createContext<DealerContextProps | undefined>(undefined)

const initialThemeIcon: ThemeIcon = {
  individual: null,
  business: null,
  nzLicense: null,
  employmentDetails: null,
  financialStanding: null,
  requestCallBack: null,
  continueApplication: null,
  buyingOptions: [],
  personalFinancialPosition: [],
  businessFinancialPosition: [],
  contact: null,
  email: null,
  timeToProcess: null
}

const initialDealerInformation: dealerInformation = {
  image: '',
  name: '',
  minimumLoanAmount: 0,
  buyingOptions: [],
  model: [],
  make: [],
  dealerId: null,
  dealerUniqueIdentifier: null,
  phoneNumber: ''
}

async function loadFont(fontUrl: string, fontWeight: string, fontType: string) {
  const fontFamilyName = 'custom'
  const fontFamilyURL = `${baseUrl}${fontUrl}`

  try {
    const fontFace = new FontFace(fontFamilyName, `url(${fontFamilyURL})`, {
      weight: fontWeight
    })

    await fontFace.load()

    document.fonts.add(fontFace)
    document.documentElement.style.setProperty('--font-family', `'${fontFamilyName}', sans-serif`)

    if (fontType === 'bold') {
      document.documentElement.style.setProperty('--font-weight-bold', fontWeight)
    } else if (fontType === 'normal') {
      document.documentElement.style.setProperty('--font-weight-normal', fontWeight)
    }
  } catch (error) {
    console.error(`Failed to load font "${fontFamilyName}":`, error)
  }
}

const DealerProvider = ({ children }: { children: ReactNode }) => {
  const [isApplicationLoaded, setIsApplicationLoaded] = useState(false)
  const [buttonShape, setButtonShape] = useState('rounded-xl')
  const [dealerInformation, setDealerInformation] = useState<dealerInformation>(initialDealerInformation)
  const [callBackEnabled, setCallBackEnabled] = useState(true)
  const [dealerThemeIcon, setDealerThemeIcon] = useState<ThemeIcon>(initialThemeIcon)
  const [isDealer, setIsDealer] = useState(false)
  const [themeLogo, setThemeLogo] = useState<string>('')

  const formatPurchaseItems = useCallback((items: PurchaseItem[] = []) => {
    if (!items || items.length === 0) return []
    return items.map(item => ({
      ...item,
      icon: item.icon ? `${baseUrl}${item.icon}` : ''
    }))
  }, [baseUrl])

  const formatOptionedIcons = useCallback((items: OptionedIcon[] = []) => {
    if (!items || items.length === 0) return []
    return items.map(item => ({
      ...item,
      icon: item.icon ? `${baseUrl}${item.icon}` : ''
    }))
  }, [baseUrl])

  const setDealerBasicThemeDetails = useCallback((themeDetails: ThemeDetails) => {
    const rootStyle = document.documentElement.style
    const { primary, secondary, tertiary, text, icon } = themeDetails.color

    // Handle theme colors
    if (primary) rootStyle.setProperty('--brand-1', primary)
    if (secondary) rootStyle.setProperty('--brand-2', secondary)
    if (tertiary) rootStyle.setProperty('--brand-3', tertiary)
    if (text) rootStyle.setProperty('--theme-text', text)
    if (icon) rootStyle.setProperty('--theme-icon', icon)

    // Handle theme logo
    setThemeLogo(themeDetails.logo ? `${baseUrl}${themeDetails.logo}` : '')

    // Handle theme fonts
    if (themeDetails.boldFont !== '') loadFont(themeDetails.boldFont, themeDetails.boldFontWeight, 'bold')
    if (themeDetails.regularFont !== '') loadFont(themeDetails.regularFont, themeDetails.regularFontWeight, 'normal')

    // Handle theme button shapes, this indicator will be used inside the button component
    if (themeDetails.buttonShape !== '') {
      if (themeDetails.buttonShape === 'Main') setButtonShape('rounded-xl')
      if (themeDetails.buttonShape === 'Square') setButtonShape('rounded-none')
      if (themeDetails.buttonShape === 'Rounded') setButtonShape('rounded-full')
    }

    const icons = themeDetails?.icon ?? initialThemeIcon
    const newDealerThemeIcon: ThemeIcon = {
      individual: icons.individual ? `${baseUrl}${icons.individual}` : '',
      business: icons.business ? `${baseUrl}${icons.business}` : '',
      nzLicense: icons.nzLicense ? `${baseUrl}${icons.nzLicense}` : '',
      employmentDetails: icons.employmentDetails ? `${baseUrl}${icons.employmentDetails}` : '',
      financialStanding: icons.financialStanding ? `${baseUrl}${icons.financialStanding}` : '',
      requestCallBack: icons.requestCallBack ? `${baseUrl}${icons.requestCallBack}` : '',
      continueApplication: icons.continueApplication ? `${baseUrl}${icons.continueApplication}` : '',
      buyingOptions: formatPurchaseItems(icons.buyingOptions),
      personalFinancialPosition: formatOptionedIcons(icons.personalFinancialPosition),
      businessFinancialPosition: formatOptionedIcons(icons.businessFinancialPosition),
      contact: icons.contact ? `${baseUrl}${icons.contact}` : '',
      email: icons.email ? `${baseUrl}${icons.email}` : '',
      timeToProcess: icons.timeToProcess ? `${baseUrl}${icons.timeToProcess}` : ''
    }

    setDealerThemeIcon(prevIcon => {
      if (deepEqual(prevIcon, newDealerThemeIcon)) {
        return prevIcon
      }
      return newDealerThemeIcon
    })
  }, [baseUrl, formatPurchaseItems])

  const toOptions = useCallback((arr?: string[]) =>
    [{ value: '', name: 'Select' }, ...(arr?.map(v => ({ value: v, name: v })) || [])]
  , [])

  const setDealer = useCallback((dealer: DealerDetails) => {
    const dealerTheme = dealer.themeDetails

    if (dealerTheme) {
      setDealerBasicThemeDetails(dealerTheme)
    }

    // Set isDealer to true when dealer is set
    setIsDealer(true)

    // Handle feature flags
    if (dealer.callBackEnabled !== undefined) setCallBackEnabled(dealer.callBackEnabled)

    const make = toOptions(dealer.make || [])
    const model =  toOptions(dealer.model || [])
    
    const dealersDetails = {
      image: dealerTheme?.image ? `${baseUrl}${dealerTheme.image}` : '',
      name: dealerTheme?.name ? dealerTheme.name : '',
      minimumLoanAmount: dealer.minimumLoanAmount,
      buyingOptions: dealer.buyingOptions.split(',')?.map((item: string) => item.trim()) || [],
      dealerId: dealer.dealerId,
      dealerUniqueIdentifier: dealer.dealerUniqueIdentifier,
      make,
      model,
      phoneNumber: dealer.dealerPhoneNumber
    }

    setDealerInformation(pre => {
      if (deepEqual(pre, dealersDetails)) {
        return pre
      }
      return dealersDetails
    })

    setIsApplicationLoaded(true)
  }, [setDealerBasicThemeDetails, setIsDealer, setCallBackEnabled, toOptions, setDealerInformation, setIsApplicationLoaded, baseUrl])

  const setDealerLoadingCompleted = useCallback((isLoaded: boolean) => {
    setIsApplicationLoaded(isLoaded)
  }, [])

  const contextValue = useMemo(() => ({
    setDealer,
    setDealerBasicThemeDetails,
    setDealerLoadingCompleted,
    dealerInformation,
    buttonShape,
    callBackEnabled,
    isApplicationLoaded,
    dealerThemeIcon,
    isDealer,
    themeLogo // Expose themeLogo in context
  }), [
    setDealer,
    setDealerBasicThemeDetails,
    setDealerLoadingCompleted,
    dealerInformation,
    buttonShape,
    callBackEnabled,
    isApplicationLoaded,
    dealerThemeIcon,
    isDealer,
    themeLogo
  ])

  return (
    <DealerContext.Provider value={contextValue}>
      {children}
    </DealerContext.Provider>
  )
}

export { DealerContext, DealerProvider }
